import { useEffect, useState } from "react";
import { createContainer } from "unstated-next";
import {
  UserInfo,
  IStudentCertifyInfo,
  UserDesktopInfo,
} from "@/utils/userinfo/types";
import {
  AccountType,
  BusinessType,
  InfoStatus,
  UserCertifiedStatus,
  UserType,
  Zone,
  DingTalkAccountType,
  UserLevel,
  HaveSubAccount,
  UserAuthStatus,
} from "project-config/constants/user-info";
import { getDomain } from "project-config/utils/domain";
import { accAdd } from "project-config/utils/acc";
import request from "project-config/services/request/request";
import i18next from "i18next";
import queryString from "querystring";
import { IStudentAuthenticationInfo } from "@/pages/profile/types/certify";
import { ONE_DAY_TIMESTAMP } from "project-config/constants";
import { isFox } from "project-config/utils/env";
import { DataCollector } from "@rayvision/data-collect";

function useGlobalStoreHook() {
  const [userInfo, setUserInfo] = useState<UserInfo>();
  const [userDesktopInfo, setUserDesktopInfo] = useState<UserDesktopInfo>();
  const [currencySymbol, setCurrencySymbol] = useState("");
  const [isCertified, setCertified] = useState(false);
  const [isSubAccount, setSubAccount] = useState(false);
  const [isDingTalk, setIsDingTalk] = useState(false);
  const [isDingTalkLeader, setIsDingTalkLeader] = useState(false);
  const [isPictureUser, setIsPictureUser] = useState(false);
  const [isAnimationUser, setIsAnimationUser] = useState(false);
  const [isForeign, setIsForeign] = useState(false);
  const [isDomestic, setDomestic] = useState(false);
  const [isPersonal, setIsPersonal] = useState(false);
  const [isStudent, setIsStudent] = useState(false);
  const [isStudentValid, setIsStudentValid] = useState(false);
  const [userCumulative, setUserCumulative] = useState(0);
  const [userBalance, setUserBalance] = useState(0);
  const [userSubAccountBalance, setSubAccountBalance] = useState(0);
  const [userBalanceTotal, setUserBalanceTotal] = useState(0);
  const [userCredit, setUserCredit] = useState(0);
  const [userCouponCount, setUserCouponCount] = useState(0);
  const [userCouponPrice, setUserCouponPrice] = useState(0);
  const [userLevel, setUserLevel] = useState(0);
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [isClientOrigin, setIsClientOrigin] = useState(false);
  const [isHideBalance, setIsHideBalance] = useState(false);
  const [isHideJobCharge, setIsHideJobCharge] = useState(false);
  const [studentCertifyInfo, setStudentCertifyInfo] =
    useState<IStudentCertifyInfo>();
  const [studentData, setStudentData] = useState<IStudentAuthenticationInfo>();
  const [isHaveSubAccount, setIsHaveSubAccount] = useState(false);
  const [isShowCreditTotal, setIsShowCreditTotal] = useState(false);
  const [showAuthenticateModal, setShowAuthenticateModal] = useState(false);
  const [showAuthStatusTip, setShowAuthStatusTip] = useState(false);
  const [isAnimationActiveOverTime, setIsAnimationActiveOverTime] =
    useState(false);
  const [is88StudioUser, setIs88StudioUser] = useState(false);
  const fetchUserData = async () => {
    const { data } = await request.post<UserInfo>(
      "/api/rendering/user/queryUser"
    );
    setUserInfo(data ?? userInfo);
    !isFox &&
      DataCollector.emit("config", {
        user_unique_id: data?.userId,
      });
    return data;
  };
  const fetchUserDesktopInfo = async () => {
    const { data } = await request.post<UserDesktopInfo>(
      "/api/rendering/user/queryUserCloudProductionInfo"
    );
    setUserDesktopInfo(data ?? userDesktopInfo);
    return data;
  };
  const fetchUserIsCustomized = async () => {
    const { data } = await request.post(
      "/api/rendering/user/checkCustomExportUser"
    );
    setIs88StudioUser(data ?? false);
    return data;
  };
  const fetchStudentCertifyInfo = async () => {
    const { data } = await request.post<IStudentAuthenticationInfo>(
      "/api/rendering/user/queryStudentAuthenticationInfo"
    );
    const noStudentVerify = !data || data.status === InfoStatus.NotVerified;
    const studentVerifyFailed = data?.status === InfoStatus.Rejected;
    const studentVerifyPending = data?.status === InfoStatus.Verifying;
    const studentBlock = isStudentValid || studentVerifyPending;
    const studentCertifyInfo = {
      noStudentVerify,
      studentVerifyFailed,
      studentVerifyPending,
      studentBlock,
    };
    setStudentCertifyInfo(studentCertifyInfo);
    setStudentData(data);
    return studentCertifyInfo;
  };
  useEffect(() => {
    void fetchStudentCertifyInfo().then(console.trace);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isStudentValid]);

  useEffect(() => {
    const searchParams = queryString.parse(window.location.search.substring(1));
    const fullScreen = searchParams.fullScreen as string;
    const custom = searchParams.custom as string;
    setIsFullScreen(fullScreen === "true");
    setIsClientOrigin(custom === "client");
  }, []);

  useEffect(() => {
    if (!userInfo) return;
    const isDomestic = userInfo.zone === Zone.Domestic;
    const isPictureUser = userInfo.businessType === BusinessType.Picture;
    const isAnimationUser = userInfo.businessType === BusinessType.Animation;
    const isStudentValid = userInfo.studentEndTime
      ? new Date().getTime() < new Date(userInfo.studentEndTime).getTime()
      : false;
    setIsHideBalance(Boolean(userInfo.hideBalance));
    setIsHideJobCharge(Boolean(userInfo.hideJobCharge));
    setCurrencySymbol(isDomestic ? "¥" : "US$");
    setCertified(UserCertifiedStatus.includes(userInfo.authStatus));
    setSubAccount(userInfo.accountType === AccountType.Sub);
    setIsDingTalk(userInfo.isDingTalk === DingTalkAccountType.Yes);
    setIsDingTalkLeader(userInfo.isLeader === DingTalkAccountType.Yes);
    setIsPictureUser(isPictureUser);
    setIsAnimationUser(isAnimationUser);
    setIsForeign(!isDomestic);
    setDomestic(isDomestic);
    setIsPersonal(userInfo.userType === UserType.Personal);
    setIsStudent(userInfo.infoStatus === InfoStatus.Passed);
    setIsStudentValid(isStudentValid);
    setIsHaveSubAccount(userInfo.haveSubAccount === HaveSubAccount.Yes);
    setShowAuthStatusTip(
      userInfo?.userAuthStatus !== UserAuthStatus.Certified && isDomestic
    );
    const userLevel = isPictureUser
      ? userInfo.pictureLever
      : isStudentValid && isPersonal
      ? UserLevel.DiamondStudent
      : userInfo.tempMemberValidityTime
      ? UserLevel.DiamondAnimationGift
      : userInfo.level;
    setUserLevel(userLevel);
    // 累计充值
    const cumulative = getDomain()
      ? userInfo.usdCumulative ?? 0
      : userInfo.rmbCumulative ?? 0;
    setUserCumulative(cumulative);

    //  当前余额
    const balance = isDomestic ? userInfo.rmbbalance : userInfo.usdbalance;
    setUserBalance(balance);

    // 子账号余额
    const subAccountBalance = isDomestic
      ? userInfo.subRmbBalanceTotal
      : userInfo.subUsdBalanceTotal;
    setSubAccountBalance(subAccountBalance);

    const totalBalance = isDomestic
      ? userInfo.rmbbalanceTotal
      : userInfo.usdbalanceTotal;
    setUserBalanceTotal(totalBalance);
    // 可用额度
    if (userInfo.rmbbalance >= 0) {
      setUserCredit(userInfo.credit);
    } else if (userInfo.credit + userInfo.rmbbalance > 0) {
      setUserCredit(accAdd(userInfo.credit, userInfo.rmbbalance));
    }

    // 是否展示总额度
    setIsShowCreditTotal((userInfo?.totalCredit ?? 0) !== 0);

    // 渲染券数量
    setUserCouponCount(userInfo.commonCouponCount || 0);

    // 渲染券总额
    setUserCouponPrice(userInfo.commonCoupon || 0);

    const now = new Date().getTime();
    const isAnimationActiveOverTime =
      Number(userInfo?.registerDate) + ONE_DAY_TIMESTAMP * 7 > now;
    setIsAnimationActiveOverTime(isAnimationActiveOverTime);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userInfo]);

  // 语言标识
  const [languageFlag, setLanguageFlag] = useState(getDomain());
  const [isZh, setIsZh] = useState(true);
  useEffect(() => {
    // 此 effect 用来兼容旧代码，请勿在别的地方设置 LANGUAGE_FLAG，
    // 项目建议使用 languageFlag，不建议使用 STORAGE.LANGUAGE_FLAG
    // Cookie.setCookie(STORAGE.LANGUAGE_FLAG, languageFlag.toString());
    setIsZh(i18next.language === "zh");
  }, [languageFlag]);

  return {
    userInfo,
    setUserInfo,
    fetchUserData,
    fetchUserDesktopInfo,
    userDesktopInfo,
    currencySymbol,
    isCertified,
    isSubAccount,
    isPictureUser,
    isAnimationUser,
    isForeign,
    isDomestic,
    isPersonal,
    isStudent,
    isStudentValid,
    userCumulative,
    userBalance,
    userSubAccountBalance,
    userBalanceTotal,
    userCredit,
    userCouponCount,
    userCouponPrice,
    languageFlag,
    setLanguageFlag,
    isZh,
    setIsZh,
    userLevel,
    isFullScreen,
    isClientOrigin,
    isDingTalk,
    studentData,
    setStudentData,
    studentCertifyInfo,
    fetchStudentCertifyInfo,
    isDingTalkLeader,
    isHideBalance,
    isHideJobCharge,
    isHaveSubAccount,
    isShowCreditTotal,
    showAuthenticateModal,
    setShowAuthenticateModal,
    isAnimationActiveOverTime,
    showAuthStatusTip,
    is88StudioUser,
    fetchUserIsCustomized,
  };
}

const GlobalStoreContainer = createContainer(useGlobalStoreHook);
const GlobalStoreProvider = GlobalStoreContainer.Provider;

export default GlobalStoreProvider;

export function useGlobalStore() {
  return GlobalStoreContainer.useContainer();
}
